import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SCATTER_PLOT_STATE_KEYS } from 'ChartApp/ts/apps/scatter-plot/state/constants';
import { ScatterPlotDataParams } from '../types/scatter-plot-service-params.type';
import { TechChartFlatAppState } from 'ChartApp/ts/types/tech-chart-common.type';
import { ScatterPlotInfoInteractive } from '../interfaces/scatter-plot-service-responses.interface';
import { LooseObject } from 'Angular/interfaces/loose-object.interface';
import { ScatterPlotStore } from 'ChartApp/ts/apps/scatter-plot/interfaces/scatter-plot-store.interface';
import { ScatterPlotDataBase } from 'ChartApp/ts/interfaces/scatter-plot-common.interface';

import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

import { convertNestedStringToBoolean } from 'Angular/utils/state-utils';
import { formFlattenedObject, formFlattenedParamString } from 'Angular/utils/urls';

import { TechChartUrlSerializer } from 'ChartApp/ts/features/tech-chart-page-shared/state/tech-chart.serializer';

@Injectable({
    providedIn: 'root',
})
export class ScatterPlotService {
    SCATTER_PLOT_PARAMS_REQUIRED = ['xAxisMetric', 'yAxisMetric'];

    serializedKeys: string[] = [];

    constructor(private ajaxService: AjaxService, private urlsService: UrlsService) {
        this.serializedKeys = SCATTER_PLOT_STATE_KEYS;
    }

    /**
     * Get a flattened object of the scatter plot state.
     *
     * @param scatterPlotState {ScatterPlotStore}
     */
    getFlattenedFormData(scatterPlotState: ScatterPlotStore): TechChartFlatAppState {
        return formFlattenedObject(scatterPlotState, this.serializedKeys, TechChartUrlSerializer);
    }

    /**
     * Check that the params exist and, depending on the plot type, have the required properties to make a backend
     * request without errors.
     * @param params - The scatter plot get params.
     * @returns Boolean describing whether all required params exist.
     * @private
     */
    private hasValidFetchParams(params: ScatterPlotDataParams): boolean {
        if (params === null || !params.securities) return false;

        for (let i = 0; i < this.SCATTER_PLOT_PARAMS_REQUIRED.length; i += 1) {
            if (!params[this.SCATTER_PLOT_PARAMS_REQUIRED[i] as keyof ScatterPlotDataParams]) return false;
        }

        return true;
    }

    /** Get json data for both types of scatter plots (point in time, timeseries) from ScatterPlotDataView.
     *  For a saved chart, specify the chart_id in params. */
    getScatterPlotData(params: ScatterPlotDataParams): Observable<ScatterPlotInfoInteractive> | null {
        if (!this.hasValidFetchParams(params)) return null;

        const urlParams = { format: 'json' };
        const url = this.urlsService.getUrl(this.urlsService.SCATTER_PLOT.SCATTER_PLOT_DATA, urlParams);

        return this.ajaxService.postFormEncodedDataObserveBody<LooseObject>(url, params).pipe(
            map((response: LooseObject) => {
                convertNestedStringToBoolean(response);
                return response as ScatterPlotInfoInteractive;
            })
        );
    }

    /**
     * Get image source url for making a request to the chart generator service.
     *
     * @param params {ScatterPlotDataParams}
     */
    getChartEmbedUrl(_params: ScatterPlotDataParams): string {
        const params = { ..._params };
        const urlExtension = this.urlsService
            .getUrl(this.urlsService.EMBEDS.SCATTER_PLOT_CHART, undefined, params)
            .replace(/#/g, '%23');
        return `${process.env.NODE_BASE_URL}${urlExtension}`;
    }

    /**
     * Get share url that will authenticate with Twitter and redirect to allow sharing a Scatter Plot.
     *
     * @param baseShareUrl {string}
     * @param scatterPlotState {ScatterPlotStore}
     */
    getScatterPlotShareUrl(
        baseShareUrl: string,
        scatterPlotState: ScatterPlotStore
    ): Observable<{ redirectUrl: string }> {
        const data = this.getFlattenedFormData(scatterPlotState);
        return this.ajaxService.postFormEncodedDataObserveBody<{ redirectUrl: string }>(baseShareUrl, data);
    }

    /**
     * Get the url to the chart image saved in the S3 bucket.
     *
     * @param params {ScatterPlotDataParams}
     */
    getChartSaveAsImageUrl(params: ScatterPlotDataParams): Observable<HttpResponse<{ image_url: string }>> {
        const baseUrl = this.urlsService.EMBEDS.SAVE_AS_IMAGE_SCATTER_PLOT_CHART;
        return this.ajaxService.postFormEncodedDataObserveResponse<{ image_url: string }>(baseUrl, params);
    }

    /**
     * Get disclosures about metrics and securities included in the chart.
     *
     * @param params {ScatterPlotDataParams}
     */
    getChartDisclosure(params: ScatterPlotDataParams): Observable<HttpResponse<string>> {
        const url = this.urlsService.DISCLOSURES.SCATTER_PLOT_DISCLOSURE;
        return this.ajaxService.postFormEncodedDataWithTextObserveResponse(url, params);
    }

    /**
     * Get scatter plot data in csv format.
     *
     * @param params
     */
    getChartCSVDataFromState(params: ScatterPlotDataParams): Observable<HttpResponse<Blob>> {
        const urlParams = { format: 'csv' };
        const url = this.urlsService.getUrl(this.urlsService.SCATTER_PLOT.SCATTER_PLOT_DATA, urlParams);
        return this.ajaxService.postFormEncodedDataWithBlobObserveResponse(url, params);
    }

    getChartDataUrlQuery(scatterPlotState: ScatterPlotDataBase): string {
        const state = { ...scatterPlotState };
        const serializedKeys = SCATTER_PLOT_STATE_KEYS.concat(['chartAnnotations']);

        return formFlattenedParamString(state, serializedKeys, true);
    }

    getChartAppUrl(scatterPlotState: ScatterPlotDataBase): string {
        return `${this.urlsService.SCATTER_PLOT.SCATTER_PLOT_PAGE}#/?${this.getChartDataUrlQuery(scatterPlotState)}`;
    }
}
